import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

import BigNumber from 'bignumber.js'
import fetchPoolChartData from 'state/info/queries/pools/chartData'
import { fetchAllPoolData, fetchAllPoolDataWithAddress, fetchAllPoolDataWithAddressOld, } from 'state/info/queries/pools/poolData'
import fetchPoolTransactions from 'state/info/queries/pools/transactions'
import { fetchGlobalChartData } from 'state/info/queries/protocol/chart'
import { fetchProtocolData } from 'state/info/queries/protocol/overview'
import fetchTopTransactions from 'state/info/queries/protocol/transactions'
import fetchTokenChartData from 'state/info/queries/tokens/chartData'
import fetchPoolsForToken from 'state/info/queries/tokens/poolsForToken'
import fetchTokenPriceData from 'state/info/queries/tokens/priceData'
import { fetchAllTokenData, fetchAllTokenDataByAddresses, fetchTokenDayData } from 'state/info/queries/tokens/tokenData'
import fetchTokenTransactions from 'state/info/queries/tokens/transactions'
import { Block, Transaction } from 'state/info/types'
import { SWRConfiguration } from 'swr'
import useSWRImmutable from 'swr/immutable'
import { getAprsForStableFarm } from 'utils/getAprsForStableFarm'
import { getDeltaTimestamps } from 'utils/getDeltaTimestamps'
import { useBlockFromTimeStampSWR } from 'views/Info/hooks/useBlocksFromTimestamps'
import { MultiChainName, MultiChainNameExtend, checkIsStableSwap, multiChainId } from './constant'
import { ChartEntry, PoolData, PriceChartEntry, ProtocolData, TokenData } from './types'

dayjs.extend(duration)

// Protocol hooks

const refreshIntervalForInfo = 15000 // 15s
const SWR_SETTINGS_WITHOUT_REFETCH = {
  errorRetryCount: 3,
  errorRetryInterval: 3000,
}
const SWR_SETTINGS: SWRConfiguration = {
  refreshInterval: refreshIntervalForInfo,
  keepPreviousData: true,
  ...SWR_SETTINGS_WITHOUT_REFETCH,
}

export const useProtocolDataSWR = (isStableSwap: boolean = false): ProtocolData | undefined => {
  const chainName = useChainNameByQuery()
  const [t24, t48] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24, t48])
  const [block24, block48] = blocks ?? []
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data: protocolData } = useSWRImmutable(
    chainName && block24 && block48 ? [`info/protocol/updateProtocolData/${type}`, chainName] : null,
    () => fetchProtocolData(chainName, block24, block48, isStableSwap),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )

  return protocolData ?? undefined
}

export const useProtocolChartDataSWR = (isStableSwap: boolean = false): ChartEntry[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data: chartData } = useSWRImmutable(
    [`info/protocol/updateProtocolChartData/${type}`, chainName],
    () => fetchGlobalChartData(chainName, isStableSwap),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )
  return chartData ?? undefined
}

export const useProtocolTransactionsSWR = (isStableSwap: boolean = false): Transaction[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data: transactions } = useSWRImmutable(
    [`info/protocol/updateProtocolTransactionsData/${type}`, chainName],
    () => fetchTopTransactions(chainName, isStableSwap),
    SWR_SETTINGS, // update latest Transactions per 15s
  )
  return transactions ?? undefined
}

export const useAllPoolDataSWR = (isStableSwap: boolean = false) => {
  const chainName = useChainNameByQuery()
  const [t24h, t48h, t7d, t14d] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24h, t48h, t7d, t14d])
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data, status } = useSWRImmutable(
    blocks && chainName && [`info/pools/data/${type}`, chainName],
    () => fetchAllPoolData(chainName, blocks, isStableSwap),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )
  return useMemo(() => {
    return { data, status }
  }, [data, status])
}

export const usePoolDatasSWR = (poolAddresses: string[], isStableSwap: boolean = false): (PoolData | undefined)[] => {
  const name = poolAddresses.join('')
  const chainName = useChainNameByQuery()
  const [t24h, t48h, t7d, t14d] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24h, t48h, t7d, t14d])
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    chainName && [`info/pool/data/${name}/${type}`, blocks, chainName],
    () => fetchAllPoolDataWithAddressOld(chainName, poolAddresses, isStableSwap, blocks),
    SWR_SETTINGS,
  )

  return useMemo(() => {
    return poolAddresses
      .map((address) => {
        return data?.[address]?.data
      })
      .filter((pool) => pool)
  }, [data, poolAddresses])
}

export const usePoolChartDataSWR = (address: string, isStableSwap: boolean = false): ChartEntry[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    [`info/pool/chartData/${address}/${type}`, chainName],
    () => fetchPoolChartData(chainName, address),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )
  return data?.data ?? undefined
}

export const usePoolTransactionsSWR = (address: string, isStableSwap: boolean = false): Transaction[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    [`info/pool/transactionsData/${address}/${type}`, chainName],
    () => fetchPoolTransactions(chainName, address),
    SWR_SETTINGS,
  )
  return data?.data ?? undefined
}

// Tokens hooks

export const useAllTokenHighLight = (targetChainName?: MultiChainNameExtend, isStableSwap: boolean = false): TokenData[] => {
  const chainNameByQuery = useChainNameByQuery()
  const chainName = targetChainName ?? chainNameByQuery
  const [t24h, t48h, t7d, t14d] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24h, t48h, t7d, t14d], undefined, undefined, chainName)
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data, isLoading } = useSWRImmutable(
    blocks && chainName && [`info/token/data/${type}`, chainName],
    () => fetchAllTokenData(chainName, blocks ?? []),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )

  const tokensWithData = useMemo(() => {
    return data
      ? Object.keys(data)
          .map((k) => {
            return data?.[k]?.data
          })
          .filter((d) => d && d.exists)
      : []
  }, [data])
  return useMemo(() => {
    return isLoading ? [] : tokensWithData ?? []
  }, [isLoading, tokensWithData])
}

export const useAllTokenDataSWR = (isStableSwap: boolean = false): {
  [address: string]: { data?: TokenData }
} => {
  const chainName = useChainNameByQuery()
  const [t24h, t48h, t7d, t14d] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24h, t48h, t7d, t14d])
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    blocks && chainName && [`info/token/data/${type}`, chainName],
    () => fetchAllTokenData(chainName, blocks ?? [], isStableSwap),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )
  return data ?? {}
}

const graphPerPage = 50

const fetcher = (addresses: string[], chainName: MultiChainName, blocks: Block[]) => {
  const times = Math.ceil(addresses.length / graphPerPage)
  const addressGroup: Array<string[]> = []
  for (let i = 0; i < times; i++) {
    addressGroup.push(addresses.slice(i * graphPerPage, (i + 1) * graphPerPage))
  }
  return Promise.all(addressGroup.map((d) => fetchAllTokenDataByAddresses(chainName, blocks, d)))
}

export const useTokenDatasSWR = (addresses?: string[], withSettings = true, isStableSwap: boolean = false): TokenData[] | undefined => {
  const name = addresses?.join('')
  const chainName = useChainNameByQuery()
  const [t24h, t48h, t7d, t14d] = getDeltaTimestamps()
  const { blocks } = useBlockFromTimeStampSWR([t24h, t48h, t7d, t14d])
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data, isLoading } = useSWRImmutable(
    blocks && chainName && [`info/token/data/${name}/${type}`, chainName],
    () => fetcher(addresses || [], chainName, blocks ?? []),
    withSettings ? SWR_SETTINGS : SWR_SETTINGS_WITHOUT_REFETCH,
  )
  const allData = useMemo(() => {
    return data && data.length > 0
      ? data.reduce((a, b) => {
          return { ...a, ...b }
        }, {})
      : {}
  }, [data])

  const tokensWithData = useMemo(() => {
    if (!addresses && allData) {
      return undefined
    }
    return addresses?.map((a) => allData?.[a]?.data)?.filter((d) => d && d.exists)
  }, [addresses, allData])

  return useMemo(() => {
    return isLoading ? [] : tokensWithData ?? undefined
  }, [isLoading, tokensWithData])
}

export const useTokenDataSWR = (address: string | undefined): TokenData | undefined => {
  const allTokenData = useTokenDatasSWR([address ?? ''])
  return allTokenData?.find((d) => d.address === address) ?? undefined
}

export const useTokenDayDataSWR = (address: string): TokenData | undefined => {
  const chainName = useChainNameByQuery()
  const { data, isLoading } = useSWRImmutable(
    chainName && address && [`info/token/daily`, address, chainName],
    () => fetchTokenDayData(chainName, address),
    SWR_SETTINGS
  )
  return data
}

export const usePoolsForTokenSWR = (address: string, isStableSwap: boolean = false): string[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    [`info/token/poolAddress/${address}/${type}`, chainName],
    () => fetchPoolsForToken(chainName, address),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )

  return data?.addresses ?? undefined
}

export const useTokenChartDataSWR = (address: string, isStableSwap: boolean = false): ChartEntry[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    address && chainName && [`info/token/chartData/${address}/${type}`, chainName],
    () => fetchTokenChartData(chainName, address),
    SWR_SETTINGS,
  )

  return data?.data ?? undefined
}

export const useTokenPriceDataSWR = (
  address: string,
  interval: number,
  timeWindow: duration.Duration,
  isStableSwap: boolean = false
): PriceChartEntry[] | undefined => {
  const startTimestamp = dayjs().subtract(timeWindow).startOf('hours').unix()
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    [`info/token/priceData/${address}/${type}`, chainName],
    () => fetchTokenPriceData(chainName, address, interval, startTimestamp),
    SWR_SETTINGS,
  )
  return data?.data ?? undefined
}

export const useTokenTransactionsSWR = (address: string, isStableSwap: boolean = false): Transaction[] | undefined => {
  const chainName = useChainNameByQuery()
  const type = isStableSwap ? 'stableSwap' : 'swap'
  const { data } = useSWRImmutable(
    [`info/token/transactionsData/${address}/${type}`, chainName],
    () => fetchTokenTransactions(chainName, address),
    SWR_SETTINGS,
  )
  return data?.data ?? undefined
}

export const useGetChainName = () => {
  const { pathname, query } = useRouter()

  const getChain = useCallback(() => {
    if (pathname.includes('eth') || query.chain === 'eth') return 'ETH'
    return 'PULSE'
  }, [pathname, query])
  const [name, setName] = useState<MultiChainName | null>(() => getChain())
  const result = useMemo(() => name, [name])

  useEffect(() => {
    setName(getChain())
  }, [getChain])

  return result
}

export const useChainNameByQuery = (): MultiChainName => {
  const { query } = useRouter()
  const chainName = useMemo(() => {
    switch (query?.chain) {
      case 'eth':
        return 'ETH'
      default:
        return 'PULSE'
    }
  }, [query])
  return chainName
}

export const useChainIdByQuery = () => {
  const chainName = useChainNameByQuery()
  const chainId = useMemo(() => {
    return multiChainId[chainName]
  }, [chainName])
  return chainId
}

const stableSwapAPRWithAddressesFetcher = async (addresses: string[]) => {
  return Promise.all(addresses.map((d) => getAprsForStableFarm(d)))
}

export const useStableSwapTopPoolsAPR = (addresses: string[], isStableSwap: boolean = false): Record<string, number> => {
  const chainName = useChainNameByQuery()
  const { data } = useSWRImmutable<BigNumber[]>(
    isStableSwap && addresses?.length > 0 && [`info/pool/stableAPRs/Addresses/`, chainName],
    () => stableSwapAPRWithAddressesFetcher(addresses),
    SWR_SETTINGS_WITHOUT_REFETCH,
  )
  const addressWithAPR = useMemo(() => {
    const result: Record<string, number> = {}
    data?.forEach((d, index) => {
      result[addresses[index]] = d?.toNumber()
    })
    return result
  }, [addresses, data])
  return useMemo(() => {
    return isStableSwap ? addressWithAPR : {}
  }, [isStableSwap, addressWithAPR])
}

export const useMultiChainPath = () => {
  const router = useRouter()
  const { chainName } = router.query
  return chainName ? `/${chainName}` : ''
}

export const useStableSwapPath = (isStableSwap: boolean = false) => {
  return isStableSwap ? '?type=stableSwap' : ''
}
